/**
 * Keeps Monaco's cached font metrics in sync with the webfont it actually
 * renders with.
 *
 * Monaco measures character width ONCE when an editor is created and caches it.
 * The editors here render in JetBrains Mono, a webfont — if it has not finished
 * loading at that moment, Monaco measures the fallback (Consolas / monospace)
 * instead. The text then paints in JetBrains Mono at a different advance width,
 * so every column position Monaco computes is wrong by a fraction of a
 * character, and the error accumulates along the line: selections stop short of
 * the text and clicking lands the caret a few characters off. It looks like a
 * selection bug; it is a measurement bug.
 *
 * `monaco.editor.remeasureFonts()` re-reads the metrics for every live editor.
 * Call it once the fonts are ready, and again whenever the configured font
 * family or size changes.
 */

let fontsReadyHooked = false;

/**
 * Re-measure once the document's webfonts have loaded. Safe to call from every
 * editor's onMount — the `document.fonts.ready` hook is installed only once,
 * and remeasureFonts applies to all editors, not just the caller.
 */
export function remeasureMonacoFontsWhenReady(monaco) {
  if (!monaco?.editor?.remeasureFonts) return;

  // Fonts may already be loaded (second editor on the page, cached font).
  // Re-measuring is cheap and idempotent, so do it unconditionally too.
  monaco.editor.remeasureFonts();

  if (fontsReadyHooked || typeof document === 'undefined' || !document.fonts) return;
  fontsReadyHooked = true;

  document.fonts.ready
    .then(() => monaco.editor.remeasureFonts())
    .catch(() => {
      // Font loading API unavailable or rejected — the unconditional
      // remeasure above is the fallback.
    });
}

/**
 * Re-measure after a font-family or font-size change. Deferred a frame so
 * Monaco has applied the new options before metrics are read back.
 */
export function remeasureMonacoFontsSoon(monaco) {
  if (!monaco?.editor?.remeasureFonts) return;
  requestAnimationFrame(() => monaco.editor.remeasureFonts());
}
